import { api } from "./api"

export type UploadedFile = {
  key: string
  fileName: string
  fileSize: number
  contentType: string
}

type PresignResponse = {
  url: string
  key: string
}

type UploadProgressCallback = (progress: number) => void

export const ALLOWED_TYPES = [
  "audio/wav",
  "audio/mpeg",
  "audio/flac",
  "audio/x-wav",
  "audio/x-flac",
]

export const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

export function validateFile(file: File): string | null {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Invalid file type. Please upload WAV, MP3, or FLAC files."
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File too large. Maximum size is ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB.`
  }
  return null
}

export async function getPresignedUrl(file: File): Promise<PresignResponse> {
  return api.post<PresignResponse>("/uploads/presign", {
    file_name: file.name,
    content_type: file.type,
    file_size: file.size,
  })
}

export async function uploadToS3(
  url: string,
  file: File,
  onProgress?: UploadProgressCallback
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error("Upload to S3 failed"))
      }
    }

    xhr.onerror = () => reject(new Error("Upload to S3 failed"))

    xhr.open("PUT", url)
    xhr.setRequestHeader("Content-Type", file.type)
    xhr.send(file)
  })
}

export async function uploadFile(
  file: File,
  onProgress?: UploadProgressCallback
): Promise<UploadedFile> {
  const { url, key } = await getPresignedUrl(file)

  await uploadToS3(url, file, onProgress)

  return {
    key,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
  }
}
